import { z } from 'zod';

export const PRODUCT_UNITS = ['kg', 'g', 'unit', 'pack'] as const;
export type ProductUnit = (typeof PRODUCT_UNITS)[number];
export const QUOTE_STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'rejected', 'expired'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
export const ORDER_STATUSES = ['new', 'confirmed', 'preparing', 'shipped', 'delivered', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const PAYMENT_STATUSES = ['unpaid', 'partial', 'paid', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const PAYMENT_METHODS = ['bank_transfer', 'credit_card', 'cash', 'check', 'bit', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const SAMPLE_STATUSES = ['preparing', 'sent', 'delivered', 'feedback_received', 'converted', 'not_relevant'] as const;
export type SampleStatus = (typeof SAMPLE_STATUSES)[number];
export const MOVEMENT_TYPES = ['purchase', 'sale', 'sample', 'adjustment', 'damage', 'return'] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

export const UNIT_LABELS: Record<ProductUnit, string> = { kg: 'ק״ג', g: 'גרם', unit: 'יחידה', pack: 'מארז' };
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'טיוטה', sent: 'נשלחה', viewed: 'נצפתה', accepted: 'אושרה', rejected: 'נדחתה', expired: 'פג תוקף',
};
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'חדש', confirmed: 'אושר', preparing: 'בהכנה', shipped: 'נשלח', delivered: 'נמסר', cancelled: 'בוטל',
};
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = { unpaid: 'לא שולם', partial: 'שולם חלקית', paid: 'שולם', refunded: 'הוחזר' };
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_transfer: 'העברה בנקאית', credit_card: 'כרטיס אשראי', cash: 'מזומן', check: 'צ׳ק', bit: 'ביט', other: 'אחר',
};
export const SAMPLE_STATUS_LABELS: Record<SampleStatus, string> = {
  preparing: 'בהכנה', sent: 'נשלחה', delivered: 'נמסרה', feedback_received: 'התקבל משוב', converted: 'הפך ללקוח', not_relevant: 'לא רלוונטי',
};
export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  purchase: 'רכש', sale: 'מכירה', sample: 'דוגמית', adjustment: 'התאמה', damage: 'פחת / נזק', return: 'החזרה',
};

/** Allowed order status transitions (forward flow; cancel from any non-final state). */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  new: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'shipped', 'cancelled'],
  preparing: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: [],
};

const money = z.coerce.number({ error: 'יש להזין מספר' }).min(0, 'ערך שלילי אינו מותר').max(10_000_000);
const qty = z.coerce.number({ error: 'יש להזין כמות' }).positive('הכמות חייבת להיות גדולה מ-0').max(1_000_000);
const pct = z.coerce.number().min(0, 'אחוז בין 0 ל-100').max(100, 'אחוז בין 0 ל-100');
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'תאריך לא תקין');
const optText = (max: number) => z.string().trim().max(max).optional().nullable().transform((v) => (v === '' ? null : v));

export const productSchema = z.strictObject({
  sku: z.string().trim().regex(/^[A-Za-z0-9._-]{1,40}$/, 'מק״ט: אותיות לועזיות, ספרות, נקודה, מקף'),
  name: z.string().trim().min(1, 'שם המוצר הוא שדה חובה').max(200),
  unit: z.enum(PRODUCT_UNITS).default('kg'),
  unitSizeG: z.coerce.number().int().positive().optional().nullable(),
  listPrice: money,
  cost: money.optional().nullable(),
  isActive: z.boolean().default(true),
  trackInventory: z.boolean().default(true),
  minStock: z.coerce.number().min(0).optional().nullable(),
  defaultSupplierId: z.uuid().optional().nullable(),
  description: optText(2000),
  tiers: z.array(z.strictObject({ minQty: qty, unitPrice: money })).max(20).default([]),
});
export type ProductInput = z.infer<typeof productSchema>;
export const productUpdateSchema = productSchema.partial().strict();

export const customerPriceSchema = z
  .strictObject({
    productId: z.uuid(),
    minQty: qty.default(1),
    unitPrice: money.optional().nullable(),
    discountPct: pct.optional().nullable(),
    isContract: z.boolean().default(false),
    validFrom: day.optional().nullable(),
    validTo: day.optional().nullable(),
    note: optText(500),
  })
  .refine((v) => (v.unitPrice == null) !== (v.discountPct == null), { message: 'יש להזין מחיר קבוע או אחוז הנחה (אחד מהם)', path: ['unitPrice'] })
  .refine((v) => !v.validFrom || !v.validTo || v.validTo >= v.validFrom, { message: 'תאריך הסיום לפני תאריך ההתחלה', path: ['validTo'] });
export type CustomerPriceInput = z.infer<typeof customerPriceSchema>;

export const lineItemSchema = z
  .strictObject({
    productId: z.uuid().optional().nullable(),
    description: optText(300),
    quantity: qty,
    discountPct: pct.default(0),
    /** Manual price override. Requires a reason; recorded in the audit log. */
    unitPrice: money.optional().nullable(),
    overrideReason: optText(300),
  })
  .refine((l) => l.productId || l.description, { message: 'יש לבחור מוצר או לכתוב תיאור', path: ['description'] })
  .refine((l) => l.productId || l.unitPrice != null, { message: 'לפריט ללא מוצר יש להזין מחיר', path: ['unitPrice'] })
  .refine((l) => l.unitPrice == null || !l.productId || !!l.overrideReason, { message: 'שינוי ידני של מחיר מחייב סיבה', path: ['overrideReason'] });
export type LineItemInput = z.infer<typeof lineItemSchema>;

const documentFields = {
  organizationId: z.uuid(),
  contactId: z.uuid().optional().nullable(),
  items: z.array(lineItemSchema).min(1, 'יש להוסיף לפחות פריט אחד').max(100),
  documentDiscountPct: pct.default(0),
  shipping: money.default(0),
  notes: optText(5000),
};

export const quoteSchema = z.strictObject({
  ...documentFields,
  validUntil: day.optional().nullable(),
  terms: optText(5000),
});
export type QuoteInput = z.infer<typeof quoteSchema>;

export const orderSchema = z.strictObject({
  ...documentFields,
  orderDate: day.optional(),
  shippingAddress: optText(500),
});
export type OrderInput = z.infer<typeof orderSchema>;

export const orderStatusSchema = z.strictObject({ status: z.enum(ORDER_STATUSES), note: optText(1000) });

export const paymentSchema = z.strictObject({
  orderId: z.uuid().optional().nullable(),
  invoiceId: z.uuid().optional().nullable(),
  amount: z.coerce.number().positive('הסכום חייב להיות חיובי').max(10_000_000),
  method: z.enum(PAYMENT_METHODS),
  paidAt: day,
  reference: optText(120),
  note: optText(1000),
});
export type PaymentInput = z.infer<typeof paymentSchema>;

export const invoiceSchema = z.strictObject({
  orderId: z.uuid(),
  issueDate: day,
  dueDate: day.optional().nullable(),
  externalProvider: optText(60),
  externalId: optText(120),
  notes: optText(1000),
});

export const sampleSchema = z.strictObject({
  organizationId: z.uuid(),
  contactId: z.uuid().optional().nullable(),
  productId: z.uuid().optional().nullable(),
  quantity: qty.default(1),
  shippingMethod: optText(120),
  trackingNumber: optText(120),
  cost: money.optional().nullable(),
  markSent: z.boolean().default(false),
  followupDays: z.coerce.number().int().min(1).max(60).optional(),
});
export type SampleInput = z.infer<typeof sampleSchema>;

export const sampleUpdateSchema = z.strictObject({
  status: z.enum(SAMPLE_STATUSES).optional(),
  shippingMethod: optText(120),
  trackingNumber: optText(120),
  feedback: optText(3000),
  followupDays: z.coerce.number().int().min(1).max(60).optional(),
});

export const stockMovementSchema = z.strictObject({
  productId: z.uuid(),
  movementType: z.enum(['purchase', 'adjustment', 'damage', 'return']),
  quantity: z.coerce.number().refine((n) => n !== 0, 'הכמות אינה יכולה להיות 0'),
  batchCode: optText(60),
  supplierId: z.uuid().optional().nullable(),
  expiresAt: day.optional().nullable(),
  unitCost: money.optional().nullable(),
  note: optText(500),
});

/** Fix a mistaken manual movement: the new quantity (same sign rules as when it was entered) and/or note. */
export const stockMovementCorrectionSchema = z.strictObject({
  quantity: z.coerce.number().refine((n) => n !== 0, 'הכמות אינה יכולה להיות 0'),
  note: optText(500),
});

/** Stock take: the quantity actually on the shelf; the difference is booked as an adjustment. */
export const stockCountSchema = z.strictObject({
  productId: z.uuid(),
  countedQuantity: z.coerce.number().min(0, 'הכמות אינה יכולה להיות שלילית'),
  note: optText(500),
});

export const stockCancelSchema = z.strictObject({ reason: optText(300) });
